// MODULES
import React from "react";
import PropTypes from "prop-types";

// CLASSES
import Session from "@classes/Session";
import Validator from "@classes/Validator";

// COMPONENTS
import DefaultFields from "../../../components/DefaultFields/DefaultFields";

// HELPERS
import {createClassName, getPropertiesByPrefix, parseDraftDataPropsToFieldState, delay} from "@helpers/utils";
import fulfillmentCheck from "./Orderinstruktion.fulfillment";
import ActivityIndicator from "../../../../../components/ActivityIndicator/ActivityIndicator";
import api from "../../../../../helpers/api";
import Button from "../../../../../components/Button/Button";
import EditableField from "../../../components/EditableField/EditableField";
import TextView from "../../../../../components/TextView/TextView";
import * as CobHelpers from "../../../Cob.helpers";
import FieldsCollection from "../../../../../components/FieldsCollection/FieldsCollection";
import Select from "../../../../../components/Select/Select";
import {localeNumber} from "../../../../../helpers/utils";
import moment from "moment";
import NumberField from "../../../components/NumberField/NumberField";
import StackLayout from "../../../../../components/layouts/StackLayout/StackLayout";

export default class Orderinstruktion extends React.PureComponent
{
	static propTypes = {
		id: PropTypes.string.isRequired,
		title: PropTypes.string.isRequired,
		session: PropTypes.instanceOf(Session).isRequired,
		sectionNumber: PropTypes.number.isRequired,
		number: PropTypes.number.isRequired,
		prevState: PropTypes.object,
		onChange: PropTypes.func.isRequired
	};

	static companyExclusive = false;
	static privateExclusive = false;
	static fields = _getFields();

	state = _getInitialState.call(this);
	productOptions = null;

	accountTypeOptions = [
		{
			"label": "Aktie- & fondkonto",
			"value": "Aktie- & fondkonto"
		},
		{
			"label": "Investeringssparkonto",
			"value": "Investeringssparkonto"
		},
		{
			"label": "Kapitalförsäkring",
			"value": "Kapitalförsäkring"
		},
		{
			"label": "Tjänstepensionsförsäkring",
			"value": "Tjänstepensionsförsäkring"
		}
	];

	transactionTypeOptions = [
		{"label": "Köp", "value": "Köp"},
		{"label": "Sälj", "value": "Sälj"}
	];

	static ALLOWED_PRODUCTS_COUNT = 60;

	async componentDidMount()
	{
		const {session, onParentCaseLoaded} = this.props;
		try
		{
			if (this.productOptions == null || this.productOptions.length === 0)
			{
				console.log("Fetching available products...");
				this.productOptions = await _fetchProducts.call(this);
			}

			// If we want to start with a blank page, don't load case data
			if (session.context.caseId === "-" || session.context.caseId == null)
			{
				this.setState({
					isLoading: false,
					showError: false,
					isBlank: true
				}, () =>
				{
					onParentCaseLoaded([]);
				});
				return;
			}

			const caseInfoResult = await api("/case/data", {
				caseId: session.context.caseId,
				authToken: session.authToken
			});

			if (!caseInfoResult)
			{
				this.setState({isLoading: false, showError: "not-found"});
			}
			else if (caseInfoResult.Signed !== "1")
			{
				this.setState({isLoading: false, showError: "not-signed"});
			}
			else if (!caseInfoResult.CaseData)
			{
				this.setState({isLoading: false, showError: "error"});
			}
			else
			{
				caseInfoResult.CaseData = JSON.parse(caseInfoResult.CaseData);
				caseInfoResult.SecondaryData = caseInfoResult.SecondaryData ? JSON.parse(caseInfoResult.SecondaryData) : null;

				if (!caseInfoResult.CaseData.data)
				{
					this.setState({isLoading: false, showError: "error"});
				}
				else if (!caseInfoResult.CaseData.data.checkbox_advice_recommendation || caseInfoResult.CaseData.data.checkbox_advice_recommendation !== "yes")
				{
					this.setState({isLoading: false, showError: "no-orderinstruktion"});
				}
				else
				{
					let accountOwner = caseInfoResult.CaseData.data.customer_firstname;
					if (caseInfoResult.CaseData.data.customer_lastname &&
						caseInfoResult.CaseData.data.customer_lastname.length > 0)
					{
						accountOwner += " ";
						accountOwner += caseInfoResult.CaseData.data.customer_lastname;
					}

					const newFields = {...this.state.fields};
					newFields.advice_instruction_account_owner = accountOwner;

					let accountNumber = caseInfoResult.CaseData.data.recommendation_for_account;
					const usedCaseData = caseInfoResult.SecondaryData ? caseInfoResult.SecondaryData : caseInfoResult.CaseData;

					if (accountNumber !== undefined)
					{
						if (isNaN(accountNumber))
						{
							if (accountNumber.includes("aktie- & fondkonto"))
							{
								accountNumber = usedCaseData.data.account_no_depa;
								newFields.advice_instruction_account_type = "Aktie- & fondkonto";
							}
							else if (accountNumber.includes("ISK"))
							{
								accountNumber = usedCaseData.data.account_no_isk;
								newFields.advice_instruction_account_type = "Investeringssparkonto";
							}
							else if (accountNumber.includes("kapitalförsäkring"))
							{
								accountNumber = usedCaseData.data.account_no_kf;
								newFields.advice_instruction_account_type = "Kapitalförsäkring";
							}
							else if (accountNumber.includes("tjänstepensionsförsäkring"))
							{
								accountNumber = usedCaseData.data.account_no_tp;
								newFields.advice_instruction_account_type = "Tjänstepensionsförsäkring";
							}
						}
						else
						{
							if (accountNumber > 500000 || accountNumber < 100000)
							{
								newFields.advice_instruction_account_type = ""; // Unknown
							}
							else if (accountNumber > 400000)
							{
								newFields.advice_instruction_account_type = "Tjänstepensionsförsäkring";
							}
							else if (accountNumber > 300000)
							{
								newFields.advice_instruction_account_type = "Kapitalförsäkring";
							}
							else if (accountNumber > 200000)
							{
								newFields.advice_instruction_account_type = "Investeringssparkonto";
							}
							else
							{
								newFields.advice_instruction_account_type = "Aktie- & fondkonto";
							}
						}

						if (!isNaN(accountNumber))
						{
							newFields.advice_instruction_account_number = accountNumber;
						}
					}

					newFields.activeSignatories = 0;
					newFields.signatories = [];
					if (usedCaseData.data.radio_customer_type === "company")
					{
						for (let i = 0; i <= 5; i++)
						{
							const isSignatory = usedCaseData.data['checkbox_signatory_' + i];
							if (isSignatory === "yes")
							{
								newFields.signatories.push(
									{
										ssn: usedCaseData.data['company_representative_sec_num_' + i],
										firstName: usedCaseData.data['company_representative_firstname_' + i],
										lastName: usedCaseData.data['company_representative_lastname_' + i],
										email: usedCaseData.data['company_representative_email_' + i],
										phone: usedCaseData.data['company_representative_phone_' + i],
										selected: true
									}
								);
							}
						}
					}
					else
					{
						newFields.signatories.push(
							{
								ssn: usedCaseData.data['customer_social_security_number'],
								firstName: usedCaseData.data['customer_firstname'],
								lastName: usedCaseData.data['customer_lastname'],
								email: usedCaseData.data['customer_email'],
								phone: usedCaseData.data['customer_phonenumber_cell'],
								selected: true
							}
						);
					}

					this.setState({
						isLoading: false,
						showError: false,
						parentCaseId: session.context.caseId,
						caseData: caseInfoResult.CaseData.data,
						fields: newFields
					}, () =>
					{
						onParentCaseLoaded(caseInfoResult.CaseData.data);
					});
				}
			}
		}
		catch (e)
		{
			this.setState({isLoading: false, showError: "error"});
		}
	}

	componentDidUpdate(prevProps, prevState)
	{
		const {props, state} = this;
		const {id, onChange} = props;

		if (prevState.fields !== state.fields)
		{
			onChange(id, {...state}, fulfillmentCheck(props, state));
		}

    if (prevProps.draftData !== props.draftData)
    {
      if (props.draftData)
      {
        // get the draftData props specific to this section only
        const sectionFieldsPrefixes = [
          "customer_",
          "checkbox_",
          "radio_",
          "company_",
          "signatory_",
          "advice_",
          "recommendation_"
        ]
        const draftDataProps = getPropertiesByPrefix(props.draftData, sectionFieldsPrefixes);

        // parse the draftData props into state.fields
        const newFieldsState = parseDraftDataPropsToFieldState(this.state.fields, draftDataProps);

        // set the signatories
        newFieldsState.signatories = [];
        for (let i = 0; i < 4; i++) {
          const newSignatory = {};
          if (draftDataProps["signatory_" + i + "_ssn"]) newSignatory.ssn = draftDataProps["signatory_" + i + "_ssn"];
          if (draftDataProps["signatory_" + i + "_firstName"]) newSignatory.firstName = draftDataProps["signatory_" + i + "_firstName"];
          if (draftDataProps["signatory_" + i + "_lastName"]) newSignatory.lastName = draftDataProps["signatory_" + i + "_lastName"];
          if (draftDataProps["signatory_" + i + "_email"]) newSignatory.email = draftDataProps["signatory_" + i + "_email"];
          if (draftDataProps["signatory_" + i + "_phone"]) newSignatory.phone = draftDataProps["signatory_" + i + "_phone"];

          //check if there's any field in the newSignatory object and push a newSignatory if so
          if (Object.keys(newSignatory).length > 0) {
            newSignatory.selected = true;
            newFieldsState.signatories.push(newSignatory);
          }
        }

        // Set the transactions
        // First let's remove any previously set transactions
        this._removeAllTransactions();

        // Then let's add the draft transactions
        for (let i = 0; i < Orderinstruktion.ALLOWED_PRODUCTS_COUNT; i++) {
          const transactionData = {};
          if (draftDataProps["recommendation_product_buy_isin_" + i]) {
            transactionData.type = "Köp";
            transactionData.isin = draftDataProps["recommendation_product_buy_isin_" + i];
            transactionData.ticker = draftDataProps["recommendation_product_buy_ticker_" + i];
            transactionData.name = draftDataProps["recommendation_product_buy_name_" + i];
            transactionData.amount = draftDataProps["recommendation_product_buy_amount_" + i].replace(" kr", "").replace(".00", "").replace(" SEK", "").replace(",00", ""); 
            this._addTransactionFromDraftData(transactionData);
          }

          if (draftDataProps["recommendation_product_sell_isin_" + i]) {
            transactionData.type = "Sälj";
            transactionData.isin = draftDataProps["recommendation_product_sell_isin_" + i];
            transactionData.ticker = draftDataProps["recommendation_product_sell_ticker_" + i];
            transactionData.name = draftDataProps["recommendation_product_sell_name_" + i];
            transactionData.amount = draftDataProps["recommendation_product_buy_amount_" + i].replace(" kr", "").replace(".00", "").replace(" SEK", "").replace(",00", "");
            this._addTransactionFromDraftData(transactionData);
          }
        }

        this.setState(
          {
            fields: {...newFieldsState}
          },
          async () =>
          {
            await delay(200);
            onChange(id, {...this.state}, fulfillmentCheck(this.props, this.state));
            if (newFieldsState.signatories.length > 0) this.props.onManualSignatoriesSet(newFieldsState.signatories);
          }
        );
      }
    }
	}

	render()
	{
		const {props, state} = this;
		const {title, partner, session} = props;
		const classes = createClassName("Cob-CustomerInformation", {
			"Cob-View": true,
			"fulfilled": state.isFulfilled
		});

		if (state.isLoading)
		{
			return (
				<div className={classes}>
					<div className="Cob-Fields">
						<h2>{title}</h2>

						<ActivityIndicator />
					</div>
				</div>
			);
		}

		if (state.showError)
		{
			return (
				<div className={classes}>
					<div className="Cob-Fields">
						<h2>{title}</h2>

						<p className={"error"}>
							{state.showError === "not-found" && (
								<React.Fragment>
									<strong>Ärendet kunde inte hittas</strong><br />
									ID {session.context.caseId}
								</React.Fragment>
							)}

							{state.showError === "no-orderinstruktion" && (
								<React.Fragment>
									<strong>Ärendet innehåller inget underlag för orderinstruktion</strong><br />
									ID {session.context.caseId}
								</React.Fragment>
							)}

							{state.showError === "not-signed" && (
								<React.Fragment>
									<strong>Ärendet är ej signerat</strong><br />
									ID {session.context.caseId}
								</React.Fragment>
							)}

							{state.showError === "error" && (
								<React.Fragment>
									<strong>Ärendet kunde inte laddas, var god kontakta kundtjänst</strong><br />
									ID {session.context.caseId}
								</React.Fragment>
							)}
						</p>

						<Button
							size="small"
							className="continue-button"
							label="Gå tillbaka"
							appearance={"primary"}
							onClick={() => window.location = partner.redirectUrl}
						/>
					</div>
				</div>
			);
		}

		let selectedAccountTypeIndex = -1;
		for (let i = 0; i < this.accountTypeOptions.length; i++)
		{
			if (state.fields.advice_instruction_account_type === this.accountTypeOptions[i].value)
			{
				selectedAccountTypeIndex = i;
				break;
			}
		}

		const transactionBuyRows = [];
		const transactionSellRows = [];
		let totalAmountBuy = 0;
		let totalAmountSell = 0;
		const numberOfActiveProducts = this._getNumberOfActiveProducts();
		const canAddTransactionDisabled = this._isTransactionDataIncomplete();

		for (let i = 0; i < numberOfActiveProducts; i++)
		{
			const transactionType = state.caseData["recommendation_product_type_" + i];
			let productAmount = state.caseData["recommendation_product_amount_" + i];
			if (productAmount)
			{
				productAmount = productAmount.replaceAll(" ", "").replaceAll("kr", "").replaceAll("SEK", "").replaceAll(",", ".");
			}
			else
			{
				productAmount = 0;
			}

			let parsedAmount = parseFloat(productAmount);
			parsedAmount = isNaN(parsedAmount) ? 0 : parsedAmount;

			if (transactionType === "Köp")
			{
				totalAmountBuy += parsedAmount;
			}
			else
			{
				totalAmountSell += parsedAmount;
			}
		}

		// Fix for JS sometimes adding a bunch of unrelated decimals
		totalAmountBuy = Math.round(totalAmountBuy * 100) / 100;
		totalAmountSell = Math.round(totalAmountSell * 100) / 100;

		for (let i = 0; i < numberOfActiveProducts; i++)
		{
			const transactionType = state.caseData["recommendation_product_type_" + i];
			const productIsin = state.caseData["recommendation_product_isin_" + i];
			const productTicker = state.caseData["recommendation_product_ticker_" + i];
			const productName = state.caseData["recommendation_product_name_" + i];
			let productAmount = state.caseData["recommendation_product_amount_" + i];
			if (productAmount)
			{
				productAmount = productAmount.replaceAll(" ", "").replaceAll("kr", "").replaceAll("SEK", "").replaceAll(",", ".");
			}

			let shareOfTotal = 0;
			if (transactionType === "Köp")
			{
				shareOfTotal = (productAmount / totalAmountBuy) * 100;
			}

			if (!productName || productName.length <= 0)
			{
				continue;
			}

			let transactionsCollection = (transactionType === "Köp" ? transactionBuyRows : transactionSellRows);
			const rowPrefix = (transactionType === "Köp" ? "buy" : "sell");

			transactionsCollection.push(
				<tr key={"row_" + i}>
					<td className={"type"}>
						{transactionType}
					</td>
					<td className={"name"}>
						{productName}
						<span className={"isin"}>
							{productIsin}
							{state.isBlank && (
								<em onClick={this._removeTransaction.bind(this, "row_" + i)}> (ta bort rad)</em>
							)}
						</span>
						<span className={"ticker"}>{productTicker}</span>
						<input type={"hidden"}
									 name={"recommendation_product_" + rowPrefix + "_name_" + transactionsCollection.length}
									 value={productName} />
						<input type={"hidden"}
									 name={"recommendation_product_" + rowPrefix + "_isin_" + transactionsCollection.length}
									 value={productIsin} />
						<input type={"hidden"}
									 name={"recommendation_product_" + rowPrefix + "_ticker_" + transactionsCollection.length}
									 value={(!productTicker || productTicker.length === 0) ? "-" : productTicker} />
					</td>
					<td className={"price"}>
						Marknadspris
						<input type={"hidden"}
									 name={"recommendation_product_" + rowPrefix + "_price_" + transactionsCollection.length}
									 value={"Marknadspris"} />
					</td>
					<td className={"validto"}>
						Tills makulerad
						<input type={"hidden"}
									 name={"recommendation_product_" + rowPrefix + "_validto_" + transactionsCollection.length}
									 value={"Tills makulerad"} />
					</td>
					<td className={"amount"}>
						<EditableField
							name={"recommendation_product_amount_" + i}
							Component={NumberField}
							unit={" kr"}
							value={productAmount}
							decimals={0}
							valid={productAmount > 0}
							onChange={this._onTableFieldChange}
						/>
						{transactionType === "Köp" && (
							<span className={"share"}>
								{shareOfTotal.toFixed(2)}%
							</span>
						)}

						<input type={"hidden"}
									 name={"recommendation_product_" + (transactionType === "Köp" ? "buy" : "sell") + "_share_" + transactionsCollection.length}
									 value={shareOfTotal.toFixed(2) + "%"} />
						<input type={"hidden"}
									 name={"recommendation_product_" + (transactionType === "Köp" ? "buy" : "sell") + "_amount_" + transactionsCollection.length}
									 value={localeNumber(productAmount) + " SEK"} />
					</td>
				</tr>
			);
		}

		const signDate = moment();

		const customerSsn = state.caseData && state.caseData.customer_social_security_number ?
			state.caseData.customer_social_security_number : session.context.customer.id;

		return (
			<div className={classes}>
				<DefaultFields fields={this.constructor.fields} />
				<input type={"hidden"} name={"relatedCaseId"} value={state.parentCaseId} />

				<div className="Cob-Fields">
					<h2 style={{marginBottom: "0"}}>{title}</h2>

					{state.isBlank && (
						<p>
							Gäller rådgivning genomförd utanför applikationen.
						</p>
					)}

					{!state.isBlank && (
						<p>
							Gäller rådgivning genomförd av rådgivare {state.caseData.partner_firstname + " " +
							state.caseData.partner_lastname}, utskickad för signering av kund {state.caseData.sign_date}.
						</p>
					)}

					<input type={"hidden"} name={"date"} value={signDate.format("YYYY-MM-DD")} />

					<h3>Depå</h3>
					<FieldsCollection
						fieldsPerRow={2}
						fields={[
							{
								render: (
									<Select
										name="advice_instruction_account_type"
										selectedIndex={selectedAccountTypeIndex}
										label="Depåtyp*"
										options={this.accountTypeOptions}
										onChange={this._onSelectChangeType}
									/>
								)
							},
							{
								render: (
									<EditableField
										name="advice_instruction_account_number"
										label={"Depånummer*"}
										value={state.fields.advice_instruction_account_number}
										valid={state.fields.advice_instruction_account_number.length > 0 ? true : null}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
                    required={!partner._data.saveDraftButton}
									/>
								)
							},
							{
								render: (
									<EditableField
										name="advice_instruction_account_owner"
										label={"Depåinnehavare*"}
										value={state.fields.advice_instruction_account_owner}
										valid={state.fields.advice_instruction_account_owner.length > 0 ? true : null}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
                    required={!partner._data.saveDraftButton}
									/>
								)
							},
							{
								render: (
									<EditableField
										name="advice_instruction_transaction_currency"
										label={"Transaktionsvaluta*"}
										value={state.fields.advice_instruction_transaction_currency}
										valid={
											state.fields.advice_instruction_transaction_currency.length > 0 ?
												state.fields.advice_instruction_transaction_currency.length === 3 : ""
										}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
                    required={!partner._data.saveDraftButton}
									/>
								)
							}
						]}
					/>

					<input type={"hidden"} name={"customer_firstname"} value={state.caseData.customer_firstname} />
					<input type={"hidden"} name={"customer_lastname"} value={state.caseData.customer_lastname} />
					<input type={"hidden"} name={"customer_social_security_number"} value={customerSsn} />
					<input type={"hidden"} name={"customer_ssn"} value={customerSsn} />
					<input type={"hidden"} name={"customer_email"} value={state.caseData.customer_email} />
					<input type={"hidden"} name={"customer_phonenumber_cell"} value={state.caseData.customer_phonenumber_cell} />

					{(state.isBlank || true) && (
						<React.Fragment>
							<h3 style={{marginTop: "40px", marginBottom: "0"}}>
								Signatärer
							</h3>

							<p>Ange den eller de personer som ska signera orderinstruktionsdokumentet.</p>

							{state.fields.signatories.map((rep, i) => (
								<React.Fragment key={"signatory" + i}>
									<h4>Signatär {i + 1}</h4>

									<FieldsCollection
										fieldsPerRow={2}
										fields={[
											{
												render: (
													<EditableField
														name={"signatory_" + i + "_firstName"}
														label={"Förnamn"}
														value={state.fields.signatories[i].firstName}
														valid={state.fields.signatories[i].firstName && state.fields.signatories[i].firstName.length > 0 ? true : null}
														onChange={this._onSignatoryChange}
														required
													/>
												)
											},
											{
												render: (
													<EditableField
														name={"signatory_" + i + "_lastName"}
														label={"Efternamn"}
														value={state.fields.signatories[i].lastName}
														valid={state.fields.signatories[i].lastName && state.fields.signatories[i].lastName.length > 0 ? true : null}
														onChange={this._onSignatoryChange}
														required
													/>
												)
											},
											{
												render: (
													<EditableField
														name={"signatory_" + i + "_ssn"}
														label={"Personnummer"}
														value={state.fields.signatories[i].ssn}
														valid={state.fields.signatories[i].ssn && state.fields.signatories[i].ssn.length > 0 ? Validator.validateSsn(state.fields.signatories[i].ssn) : null}
														onChange={this._onSignatoryChange}
														required
													/>
												)
											},
											{
												render: (
													<EditableField
														name={"signatory_" + i + "_email"}
														label={"E-postadress"}
														value={state.fields.signatories[i].email}
														valid={state.fields.signatories[i].email && state.fields.signatories[i].email.length > 0 ? Validator.validateEmail(state.fields.signatories[i].email) : null}
														onChange={this._onSignatoryChange}
														required
													/>
												)
											},
											{
												render: (
													<EditableField
														name={"signatory_" + i + "_phone"}
														label={"Telefonnummer"}
														value={state.fields.signatories[i].phone}
														valid={state.fields.signatories[i].phone && state.fields.signatories[i].phone.length > 0 ? true : null}
														onChange={this._onSignatoryChange}
													/>
												)
											}
										]
										} />
								</React.Fragment>
							))}

							<StackLayout fullWidth style={{marginTop: "20px"}}>
								<Button
									size="small"
									label="Lägg till signatär"
									appearance={state.fields.signatories.length >= 4 ? "medium" : "primary"}
									disabled={state.fields.signatories.length >= 4}
									onClick={this._addSignatory}
									filled
								/>

								{state.fields.signatories.length > 1 && (
									<Button
										size="small"
										label="Ta bort sista"
										appearance={"danger"}
										onClick={this._removeSignatory}
									/>
								)}
							</StackLayout>
						</React.Fragment>
					)}

					<h3 style={{marginTop: "40px", marginBottom: "10px"}}>
						Transaktioner
					</h3>

					<h4>Köp</h4>
					<table className={"orderinstruktion"}>
						<thead>
						<tr>
							<td className={"type"}>Typ</td>
							<td className={"name"}>Värdepapper</td>
							<td className={"price"}>Pris</td>
							<td className={"validto"}>Giltighet</td>
							<td className={"amount"}>Belopp</td>
						</tr>
						</thead>
						<tbody>
						{transactionBuyRows}
						<tr className={"sum"}>
							<td colSpan="4">Summa</td>
							<td className={"amount"}>
								{localeNumber(totalAmountBuy) + " SEK"}
								<input type={"hidden"} name={"recommendation_product_buy_sum_total"}
											 value={localeNumber(totalAmountBuy) + " SEK"} />
							</td>
						</tr>
						</tbody>
					</table>

					<h4 style={{marginTop: "40px"}}>Sälj</h4>
					<table className={"orderinstruktion"} style={{marginBottom: "20px"}}>
						<thead>
						<tr>
							<td className={"type"}>Typ</td>
							<td className={"name"}>Värdepapper</td>
							<td className={"price"}>Pris</td>
							<td className={"validto"}>Giltighet</td>
							<td className={"amount"}>Belopp</td>
						</tr>
						</thead>
						<tbody>
						{transactionSellRows}
						<tr className={"sum"}>
							<td colSpan="4">Summa</td>
							<td className={"amount"}>
								{localeNumber(totalAmountSell) + " SEK"}
								<input type={"hidden"} name={"recommendation_product_sell_sum_total"}
											 value={localeNumber(totalAmountSell) + " SEK"} />
							</td>
						</tr>
						</tbody>
					</table>

					{state.isBlank && numberOfActiveProducts < Orderinstruktion.ALLOWED_PRODUCTS_COUNT && (
						<React.Fragment>
							<h4>Lägg till köp- eller säljtransaktion</h4>

							<div className={"citroneer-product"}>
								<div className={"transtype"}>
									<Select
										name="add_product_type"
										label="Typ"
										selectedIndex={this.transactionTypeOptions.map(c => c.value).indexOf(state.fields.add_product_type)}
										options={this.transactionTypeOptions}
										onChange={this._onTransTypeChange}
									/>
								</div>
								<div className={"product"}>
									<Select
										className="NameIsinSelect"
										label="Produktnamn & ISIN"
										inputPlaceholder="Sök på namn eller ISIN"
										options={this.productOptions}
										selectedIndex={state.fields.add_product_index}
										customFilterer={this._nameIsinFilterer}
										renderOption={this._renderNameIsinOption}
										renderSelectedOption={this._renderNameIsinOption}
										onChange={this._onNameIsinChange}
									/>
								</div>
								<div className={"amount"}>
									<NumberField label="Ange belopp"
															 name={"add_product_amount"}
															 unit=" kr"
															 value={state.fields.add_product_amount}
															 valid={state.fields.add_product_amount.length === 0 ? null : state.fields.add_product_amount.length > 0}
															 onChange={this._onAmountChange}
									/>
								</div>
							</div>

							<Button
								size="small"
								label="Lägg till"
								appearance={"primary"}
								onClick={this._addTransaction}
								disabled={canAddTransactionDisabled}
								filled
							/>
						</React.Fragment>
					)}

					{numberOfActiveProducts <= 0 && (
						<p>
							Åtminstone en köp- eller säljtransaktion måste finnas med i orderinstruktionen.
						</p>
					)}

					<h3 style={{marginTop: "60px"}}>
						Implementering
					</h3>

					<EditableField
						name="advice_instruction_implementation"
						Component={TextView}
						disableEnterKey={true}
						label={`${2000 - state.fields.advice_instruction_implementation.length} tecken kvar`}
						value={state.fields.advice_instruction_implementation}
						maxLength={2000}
						valid={
							state.fields.advice_instruction_implementation.length > 0 &&
							state.fields.advice_instruction_implementation.length <= 2000 ? true : null
						}
						resize="vertical"
						onChange={this._onFieldChange}
						onBlur={this._onFieldBlur}
						style={{maxWidth: "none"}}
					/>
				</div>
			</div>
		);
	}

	_onSelectChangeType = (option, index) =>
	{
		CobHelpers.onViewFieldChange.call(this, "advice_instruction_account_type", option.value);
	};

	_onTransTypeChange = (option, index) =>
	{
		CobHelpers.onViewFieldChange.call(this, "add_product_type", option.value);
	};

	_onAmountChange = (value) =>
	{
		CobHelpers.onViewFieldChange.call(this, "add_product_amount", value);
	};

	_onNameIsinChange = (option, index) =>
	{
		const newFields = {...this.state.fields};
		newFields.add_product_index = index;
		newFields.add_product_name = option.Name;
		newFields.add_product_isin = option.Isin;
		newFields.add_product_ticker = option.Ticker;
		this.setState({fields: newFields});
	};

	_fieldValidator = (name, value) =>
	{
		return value.length > 0;
	};

	_onFieldChange = (name, value) =>
	{
		CobHelpers.onViewFieldChange.call(this, name, value);
	};

	_addSignatory = () =>
	{
		const signatories = this.state.fields.signatories;
		signatories.push(
			{
				ssn: "",
				firstName: "",
				lastName: "",
				email: "",
				phone: "",
				selected: true
			}
		);
		const newFields = {...this.state.fields};
		newFields.signatories = signatories;
		this.setState({fields: newFields});
	};

	_removeSignatory = () =>
	{
		const signatories = this.state.fields.signatories;
		signatories.pop();
		const newFields = {...this.state.fields};
		newFields.signatories = signatories;
		this.setState({fields: newFields});
	};

	_onSignatoryChange = (name, value) =>
	{
		const signatoryIndex = name.substring(10).substring(0, 1);
		const signatoryFieldKey = name.substring(12);

		const signatories = this.state.fields.signatories;

		if (signatoryFieldKey === "firstName")
		{
			signatories[[signatoryIndex]].firstName = value;
		}
		else if (signatoryFieldKey === "lastName")
		{
			signatories[[signatoryIndex]].lastName = value;
		}
		else if (signatoryFieldKey === "phone")
		{
			signatories[[signatoryIndex]].phone = value;
		}
		else if (signatoryFieldKey === "ssn")
		{
			signatories[[signatoryIndex]].ssn = value;
		}
		else if (signatoryFieldKey === "email")
		{
			signatories[[signatoryIndex]].email = value;
		}

		const newFields = {...this.state.fields};
		newFields.signatories = signatories;

		this.setState({fields: newFields}, () =>
		{
			this.props.onManualSignatoriesSet(signatories);
		});
	};

	_onFieldBlur = (name) =>
	{
		CobHelpers.onViewFieldBlur.call(this, name);
	};

	_onTableFieldChange = (name, value) =>
	{
		const newCaseData = {...this.state.caseData, [name]: value};
		this.setState({caseData: newCaseData});
		CobHelpers.onViewFieldChange.call(this, name, value);
	};

	_getNumberOfActiveProducts = () =>
	{
		let numberOfActiveProducts = 0;
		for (let i = 0; i < Orderinstruktion.ALLOWED_PRODUCTS_COUNT; i++)
		{
			const transactionType = this.state.caseData["recommendation_product_type_" + i];
			if (transactionType !== undefined && transactionType !== "")
			{
				numberOfActiveProducts++;
			}
		}
		return numberOfActiveProducts;
	};

	_renderNameIsinOption = ({Name, Isin, Type}) => (
		<React.Fragment>
			<div className="name">{Name}</div>
			<div className="type">{Type}</div>
			<div className="isin">{Isin}</div>
		</React.Fragment>
	);

	_nameIsinFilterer = ({option}, input) =>
	{
		input = input.toLowerCase();

		let matchesIsinNumber = false;
		if (option.Isin)
		{
			const isin = option.Isin.toLowerCase();
			matchesIsinNumber = isin.indexOf(input) === 0;
		}

		const name = option.Name.toLowerCase();
		const type = option.Type.toLowerCase();
		return name.indexOf(input) >= 0 || matchesIsinNumber || type.indexOf(input) >= 0;
	};

	_isTransactionDataIncomplete = () =>
	{
		return this.state.fields.add_product_amount.length <= 0 ||
			this.state.fields.add_product_index <= -1 ||
			this.state.fields.add_product_type.length <= 0;
	};

	_addTransactionFromDraftData = transactionData =>
	{
		const productIndex = this._getNumberOfActiveProducts();

		const newStateCaseData = this.state.caseData;
		newStateCaseData["recommendation_product_type_" + productIndex] = transactionData.type;
		newStateCaseData["recommendation_product_isin_" + productIndex] = transactionData.isin;
		newStateCaseData["recommendation_product_ticker_" + productIndex] = transactionData.ticker;
		newStateCaseData["recommendation_product_name_" + productIndex] = transactionData.name;
		newStateCaseData["recommendation_product_amount_" + productIndex] = transactionData.amount;

		const newFields = {...this.state.fields};
		newFields.add_product_index = -1;
		newFields.add_product_type = "";
		newFields.add_product_ticker = "";
		newFields.add_product_name = "";
		newFields.add_product_amount = "";
		this.setState({caseData: newStateCaseData, fields: newFields});
	};

	_addTransaction = () =>
	{
		if (this._isTransactionDataIncomplete())
		{
			console.log("Transaction data was incomplete, cannot add product");
			return;
		}

		const productIndex = this._getNumberOfActiveProducts();

		const newStateCaseData = this.state.caseData;
		newStateCaseData["recommendation_product_type_" + productIndex] = this.state.fields.add_product_type;
		newStateCaseData["recommendation_product_isin_" + productIndex] = this.state.fields.add_product_isin;
		newStateCaseData["recommendation_product_ticker_" + productIndex] = this.state.fields.add_product_ticker;
		newStateCaseData["recommendation_product_name_" + productIndex] = this.state.fields.add_product_name;
		newStateCaseData["recommendation_product_amount_" + productIndex] = this.state.fields.add_product_amount;

		const newFields = {...this.state.fields};
		newFields.add_product_index = -1;
		newFields.add_product_type = "";
		newFields.add_product_ticker = "";
		newFields.add_product_name = "";
		newFields.add_product_amount = "";
		this.setState({caseData: newStateCaseData, fields: newFields});
	};

	_removeTransaction = (row) =>
	{
		// create a new array that doesn't contain this row
		const rowToRemoveIndex = row.substring(4);

		const newStateCaseData = this.state.caseData;
		newStateCaseData["recommendation_product_type_" + rowToRemoveIndex] = "";
		newStateCaseData["recommendation_product_isin_" + rowToRemoveIndex] = "";
		newStateCaseData["recommendation_product_ticker_" + rowToRemoveIndex] = "";
		newStateCaseData["recommendation_product_name_" + rowToRemoveIndex] = "";
		newStateCaseData["recommendation_product_amount_" + rowToRemoveIndex] = "";

		const nextIndex = parseInt(rowToRemoveIndex, 10) + 1;
		if (nextIndex < Orderinstruktion.ALLOWED_PRODUCTS_COUNT)
		{
			for (let i = nextIndex; i < Orderinstruktion.ALLOWED_PRODUCTS_COUNT; i++)
			{
				newStateCaseData["recommendation_product_type_" + (i - 1)] = newStateCaseData["recommendation_product_type_" + i];
				newStateCaseData["recommendation_product_isin_" + (i - 1)] = newStateCaseData["recommendation_product_isin_" + i];
				newStateCaseData["recommendation_product_ticker_" + (i - 1)] = newStateCaseData["recommendation_product_ticker_" + i];
				newStateCaseData["recommendation_product_name_" + (i - 1)] = newStateCaseData["recommendation_product_name_" + i];
				newStateCaseData["recommendation_product_amount_" + (i - 1)] = newStateCaseData["recommendation_product_amount_" + i];
			}
		}

		// Update fields since this will trigger a rerender
		const newFields = {...this.state.fields};
		newFields.product_changes = this.state.fields.product_changes + 1;
		this.setState({caseData: newStateCaseData, fields: newFields});
	};

	_removeAllTransactions = async () =>
	{
		const newStateCaseData = this.state.caseData;

    for (let i = 0; i < Orderinstruktion.ALLOWED_PRODUCTS_COUNT; i++)
    {
      newStateCaseData["recommendation_product_type_" + i] = "";
      newStateCaseData["recommendation_product_isin_" + i] = "";
      newStateCaseData["recommendation_product_ticker_" + i] = "";
      newStateCaseData["recommendation_product_name_" + i] = "";
      newStateCaseData["recommendation_product_amount_" + i] = "";
    }

		// Update fields since this will trigger a rerender
		const newFields = {...this.state.fields};
		newFields.product_changes = this.state.fields.product_changes + 1;
		this.setState({caseData: newStateCaseData, fields: newFields});
	};
}

// PRIVATE FUNCTIONS
function _getFields()
{
	return [];
}

function _fetchProducts()
{
	return new Promise(async (resolve, reject) =>
	{
		try
		{
			const result = await api("/instruments", {});
			resolve(result);
		}
		catch (e)
		{
			console.error(e.message);
			reject();
		}
	});
}

function _getInitialState()
{
	if (this.props.prevState)
	{
		return {...this.props.prevState};
	}

	return {
		isLoading: true,
		showError: true,
		isBlank: false,
		parentCaseId: "",
		caseData: [],
		activeSignatories: 1,
		validFields: {},
		fields: {
			product_changes: 0,

			advice_instruction_account_type: "",
			advice_instruction_account_number: "",
			advice_instruction_account_owner: "",
			advice_instruction_transaction_currency: "SEK",
			advice_instruction_implementation: "",

			add_product_type: "",
			add_product_index: -1,
			add_product_name: "",
			add_product_isin: "",
			add_product_ticker: "",
			add_product_amount: "",

			signatories: [
				{
					ssn: "",
					firstName: "",
					lastName: "",
					email: "",
					phone: "",
					selected: true
				}
			]
		}
	};
}


// WEBPACK FOOTER //
// ./src/routes/Cob/views/Citroneer/orderinstruktion/Orderinstruktion.js